import {
  ArrowLeftOutlined,
  CheckOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, DatePicker, Form, Input, Popconfirm, Tag, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useMemo, useReducer, useRef, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { UnsavedChangesGuard } from '../hooks/useUnsavedChangesPrompt';
import { apiErrorMessage } from '../api/client';
import { shipmentsApi, skladsApi } from '../api/endpoints';
import { invalidateMoneyData, queryKeys } from '../api/queryKeys';
import type { ManualShipmentBody, Shipment } from '../api/types';
import { AmountInput } from '../components/AmountInput';
import { ModalCombobox } from '../components/ModalCombobox';
import { ModalSelect } from '../components/ModalSelect';
import { useStoreContext } from '../context/StoreContext';
import { OWED_TO_OWNER, OWNER_OWES } from '../lib/balanceColors';
import { formatKopeks, isValidRubInput, kopeksToRubString, rubToKopeks } from '../lib/money';
import { normalizeName } from '../lib/productName';
import { returnToOf } from '../lib/returnTo';
import { formatQuantityMilli, isValidQtyInput, qtyToMilli } from '../lib/quantity';
import {
  ExtraSkladItems,
  extraGroupsComplete,
  extraGroupsFromItems,
  extraGroupsToItems,
  extraSideTotal,
  type ExtraGroup,
} from './shipment/ExtraSkladItems';

/** Mirrors backend shipment-math.lineSum — the two MUST round identically. */
function lineSum(quantityMilli: number, priceKopeks: number): number {
  return Math.round((quantityMilli * priceKopeks) / 1000);
}

interface ItemRow {
  name?: string;
  qty?: string; // "1,5" — string math only (SPEC §2)
  buy?: string; // rubles
  sell?: string; // rubles
}

interface EditorValues {
  storeId: string;
  skladId: string;
  date: Dayjs;
  msName?: string;
  items: ItemRow[];
}

const parsedQty = (row: ItemRow): number | null =>
  row.qty && isValidQtyInput(row.qty) && qtyToMilli(row.qty) > 0 ? qtyToMilli(row.qty) : null;
const parsedPrice = (value?: string): number | null =>
  value && value !== '-' && isValidRubInput(value) ? rubToKopeks(value) : null;

/** Per-mirror «Моя цена» drafts (ruble strings) for transferred-in lines. */
type MovedInDraft = Record<string, string>;

/** Stable string of everything the owner can edit — compared against a saved
 * baseline to decide whether there are unsaved changes (SPEC: nav guard). */
function serializeEditor(
  values: Partial<EditorValues>,
  extra: ExtraGroup[],
  movedIn: MovedInDraft,
): string {
  return JSON.stringify({
    movedIn,
    storeId: values.storeId ?? '',
    skladId: values.skladId ?? '',
    date: dayjs.isDayjs(values.date) ? values.date.format('YYYY-MM-DD') : '',
    msName: values.msName ?? '',
    items: (values.items ?? []).map((r) => ({
      name: r?.name ?? '',
      qty: r?.qty ?? '',
      buy: r?.buy ?? '',
      sell: r?.sell ?? '',
    })),
    extra: extra.map((g) => ({
      skladId: g.skladId,
      rows: g.rows.map((r) => ({
        name: r.name ?? '',
        qty: r.qty ?? '',
        buy: r.buy ?? '',
        sell: r.sell ?? '',
      })),
    })),
  });
}

/**
 * Manual накладная editor (SPEC §1): create or edit a draft for any
 * non-MoySklad sklad — товары with «за сколько взял» and «за сколько отдал»
 * per line, live totals + наценка. «Провести» saves and posts in one go.
 */
export function ManualShipmentEditor({ shipment }: { shipment?: Shipment }): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Opened from a jadval cell: «Назад», a posted document and a deleted draft
  // all return to that table (month kept) instead of the shipments list.
  const returnTo = returnToOf(useLocation());
  const backTo = returnTo ?? '/shipments';
  const { storeId: ctxStoreId, scopeStores } = useStoreContext();
  const [form] = Form.useForm<EditorValues>();
  // Items from OTHER sklads (SPEC §1 multi-sklad) — the primary Form.List below
  // holds the header-sklad items; these are additional per-sklad groups.
  const initialExtra = useMemo<ExtraGroup[]>(
    () =>
      shipment
        ? extraGroupsFromItems(shipment.items).filter((g) => g.skladId !== shipment.skladId)
        : [],
    [shipment],
  );
  const [extraGroups, setExtraGroups] = useState<ExtraGroup[]>(initialExtra);
  // Lines transferred INTO this document from another store (MOVED_IN mirrors).
  // They are owned by the transfer: name/qty/buy are fixed (buy = store A's
  // cost), only «Моя цена» is editable here — saved via savePrices, never via
  // the manual replace (updateManual leaves mirrors untouched).
  const movedInItems = useMemo(
    () => shipment?.items.filter((i) => i.origin === 'MOVED_IN') ?? [],
    [shipment],
  );
  const initialMovedIn = useMemo<MovedInDraft>(
    () =>
      Object.fromEntries(
        movedInItems.map((i) => [
          i.id,
          i.issuePriceKopeks != null ? kopeksToRubString(i.issuePriceKopeks) : '',
        ]),
      ),
    [movedInItems],
  );
  const [movedInDraft, setMovedInDraft] = useState<MovedInDraft>(initialMovedIn);
  const movedInDraftRef = useRef(movedInDraft);
  movedInDraftRef.current = movedInDraft;

  const initialValues: EditorValues = shipment
    ? {
        storeId: shipment.storeId,
        skladId: shipment.skladId!,
        date: dayjs(shipment.msDate, 'YYYY-MM-DD'),
        msName: shipment.msName,
        // Only header-sklad items here; other-sklad items load into extraGroups,
        // transferred-in mirrors are listed separately below (not owner rows).
        items: shipment.items
          .filter(
            (item) =>
              item.origin !== 'MOVED_IN' && (!item.skladId || item.skladId === shipment.skladId),
          )
          .map((item) => ({
            name: item.name,
            qty: formatQuantityMilli(item.quantityMilli),
            buy: item.buyPriceKopeks != null ? kopeksToRubString(item.buyPriceKopeks) : '',
            sell: item.issuePriceKopeks != null ? kopeksToRubString(item.issuePriceKopeks) : '',
          })),
      }
    : {
        storeId: ctxStoreId ?? ('' as string),
        skladId: '',
        date: dayjs(),
        items: [{}],
      };

  // ── Unsaved-changes tracking (nav guard) ──────────────────────────────────
  // Baseline = last-saved serialized state; dirty = current differs. `markClean`
  // resets it before an intentional post-save navigate (so we never block our own
  // redirect) and after a save that stays on the page.
  const savedRef = useRef(serializeEditor(initialValues, initialExtra, initialMovedIn));
  const dirtyRef = useRef(false);
  const extraGroupsRef = useRef(extraGroups);
  extraGroupsRef.current = extraGroups;
  const [, forceTick] = useReducer((x: number) => x + 1, 0);
  const markClean = (): void => {
    savedRef.current = serializeEditor(
      form.getFieldsValue(true) as EditorValues,
      extraGroupsRef.current,
      movedInDraftRef.current,
    );
    dirtyRef.current = false;
  };

  const skladsQuery = useQuery({ queryKey: queryKeys.sklads(), queryFn: () => skladsApi.list() });
  // Previously-used product names → item-name autocomplete (don't retype).
  const productNamesQuery = useQuery({
    queryKey: queryKeys.shipmentProductNames,
    queryFn: () => shipmentsApi.productNames(),
    staleTime: 5 * 60_000,
  });

  // Price memory for the selected store: remembered buy+sell per product name,
  // auto-filled when a product is added. Follows the store the owner picks.
  const watchedStoreId = Form.useWatch('storeId', form) as string | undefined;
  const lastPricesQuery = useQuery({
    queryKey: queryKeys.shipmentLastPrices(watchedStoreId ?? ''),
    queryFn: () => shipmentsApi.lastPrices(watchedStoreId!),
    enabled: !!watchedStoreId,
    staleTime: 60_000,
  });

  /** Fill a row's buy+sell from memory — only empty fields, never clobbering. */
  const fillFromMemory = (index: number, rawName: string): void => {
    const mem = lastPricesQuery.data?.[normalizeName(rawName)];
    if (!mem) return;
    if (!form.getFieldValue(['items', index, 'buy']) && mem.buyPriceKopeks != null) {
      form.setFieldValue(['items', index, 'buy'], kopeksToRubString(mem.buyPriceKopeks));
    }
    if (!form.getFieldValue(['items', index, 'sell']) && mem.issuePriceKopeks != null) {
      form.setFieldValue(['items', index, 'sell'], kopeksToRubString(mem.issuePriceKopeks));
    }
  };

  // Live totals: recomputed on every keystroke from the raw string values.
  const watched = Form.useWatch('items', form) as ItemRow[] | undefined;
  const rows = (watched ?? []).filter(Boolean);
  const buyTotal = rows.reduce((sum, row) => {
    const qty = parsedQty(row);
    const buy = parsedPrice(row.buy);
    return sum + (qty != null && buy != null ? lineSum(qty, buy) : 0);
  }, 0);
  const sellTotal = rows.reduce((sum, row) => {
    const qty = parsedQty(row);
    const sell = parsedPrice(row.sell);
    return sum + (qty != null && sell != null ? lineSum(qty, sell) : 0);
  }, 0);
  // Other-sklad items add to both sides of the document.
  const extraBuyTotal = extraSideTotal(extraGroups, 'buy');
  const extraSellTotal = extraSideTotal(extraGroups, 'sell');
  // Transferred-in mirrors post as their own sklad leg (backend buildLegs):
  // purchase = active qty × A's buy, issue = active qty × the «Моя цена» typed here.
  const movedInBuyTotal = movedInItems.reduce(
    (sum, item) => sum + lineSum(item.activeQuantityMilli, item.buyPriceKopeks ?? 0),
    0,
  );
  const movedInSellTotal = movedInItems.reduce((sum, item) => {
    const sell = parsedPrice(movedInDraft[item.id]);
    return sum + (sell != null ? lineSum(item.activeQuantityMilli, sell) : 0);
  }, 0);
  const totalBuy = buyTotal + extraBuyTotal + movedInBuyTotal;
  const totalSell = sellTotal + extraSellTotal + movedInSellTotal;

  // Mirror «Моя цена» inputs with a valid value → savePrices payload.
  const collectMovedInPriced = (): { itemId: string; issuePriceKopeks: number }[] =>
    movedInItems.flatMap((item) => {
      const sell = parsedPrice(movedInDraft[item.id]);
      return sell != null ? [{ itemId: item.id, issuePriceKopeks: sell }] : [];
    });

  const toBody = (values: EditorValues): ManualShipmentBody => ({
    date: values.date.format('YYYY-MM-DD'),
    storeId: values.storeId,
    skladId: values.skladId,
    ...(values.msName?.trim() ? { msName: values.msName.trim() } : {}),
    items: [
      // Header-sklad items (no per-line skladId → falls back to skladId above).
      ...values.items.filter(Boolean).map((row) => ({
        name: row.name!.trim(),
        quantityMilli: qtyToMilli(row.qty!),
        buyPriceKopeks: parsedPrice(row.buy),
        issuePriceKopeks: parsedPrice(row.sell),
      })),
      // Items from other sklads, each carrying its own skladId.
      ...extraGroupsToItems(extraGroups),
    ],
  });

  const onError = (error: Error): void => {
    message.error(apiErrorMessage(error) ?? t('common.requestError'));
  };

  const invalidateShipments = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['shipments'] });
    // «Аналитика по товарам» reads draft lines too (status=all).
    void queryClient.invalidateQueries({ queryKey: ['reports', 'products'] });
  };

  // Owner rows go through the manual replace; the mirrors' «Моя цена» through
  // savePrices AFTER it (the replace leaves MOVED_IN lines untouched).
  const save = async (values: EditorValues): Promise<Shipment> => {
    const saved = shipment
      ? await shipmentsApi.updateManual(shipment.id, toBody(values))
      : await shipmentsApi.createManual(toBody(values));
    const movedIn = shipment ? collectMovedInPriced() : [];
    return movedIn.length > 0 ? shipmentsApi.savePrices(saved.id, { items: movedIn }) : saved;
  };

  const saveMutation = useMutation({
    mutationFn: save,
    onSuccess: (saved) => {
      message.success(t('common.saved'));
      markClean();
      invalidateShipments();
      if (!shipment) navigate(`/shipments/${saved.id}`, { replace: true });
    },
    onError,
  });

  const postMutation = useMutation({
    mutationFn: async (values: EditorValues) => {
      const saved = await save(values);
      return shipmentsApi.post(saved.id);
    },
    onSuccess: (result) => {
      const sklads = result.legs
        .map((leg) =>
          t('quick.skladDebtPart', {
            sklad: leg.purchase.sklad?.name ?? '',
            skladDebt: formatKopeks(leg.skladDebt),
          }),
        )
        .join('; ');
      message.success(
        t('quick.combinedSuccessMulti', {
          sklads,
          store: result.legs[0]?.issue?.store?.name ?? '',
          storeBalance: formatKopeks(result.storeBalance),
        }),
        8,
      );
      markClean();
      invalidateMoneyData(queryClient);
      invalidateShipments();
      navigate(returnTo ?? `/shipments/${result.shipment.id}`, { replace: true });
    },
    onError,
  });

  const deleteMutation = useMutation({
    mutationFn: () => shipmentsApi.remove(shipment!.id),
    onSuccess: () => {
      message.success(t('shipments.draftDeleted'));
      markClean();
      invalidateShipments();
      navigate(backTo, { replace: true });
    },
    onError,
  });

  // A transfer shell may hold only mirrors — no owner rows required then.
  const allPostable =
    (rows.length > 0 || movedInItems.length > 0) &&
    rows.every(
      (row) =>
        row.name?.trim() &&
        parsedQty(row) != null &&
        parsedPrice(row.buy) != null &&
        parsedPrice(row.sell) != null,
    ) &&
    extraGroupsComplete(extraGroups) &&
    movedInItems.every(
      (item) => item.activeQuantityMilli === 0 || parsedPrice(movedInDraft[item.id]) != null,
    ) &&
    totalBuy > 0 &&
    totalSell > 0;

  // Recomputed every render (form changes bump `forceTick`; extraGroups is state).
  // Before the form store initializes, getFieldsValue is empty → fall back to the
  // baseline so a fresh screen never reads as dirty.
  const currentRaw = form.getFieldsValue(true) as EditorValues;
  const current = serializeEditor(
    Object.keys(currentRaw).length ? currentRaw : initialValues,
    extraGroups,
    movedInDraft,
  );
  dirtyRef.current = current !== savedRef.current;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <UnsavedChangesGuard shouldBlock={() => dirtyRef.current} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(backTo)}>
          {t('common.back')}
        </Button>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {shipment
            ? t('shipments.rowTitle', { name: shipment.msName })
            : t('shipments.createManual')}
        </Typography.Title>
      </div>

      <Form<EditorValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={initialValues}
        onValuesChange={() => forceTick()}
        onFinish={(values) => saveMutation.mutate(values)}
      >
        <Card size="small" style={{ marginBottom: 12 }}>
          <Form.Item
            name="storeId"
            label={t('common.store')}
            rules={[{ required: true, message: t('common.storeRequired') }]}
          >
            <ModalSelect
              size="large"
              placeholder={t('quick.storePlaceholder')}
              title={t('common.store')}
              options={scopeStores.map((s) => ({ value: s.id, label: s.name }))}
            />
          </Form.Item>
          <Form.Item
            name="skladId"
            label={t('common.sklad')}
            rules={[{ required: true, message: t('common.skladRequired') }]}
          >
            <ModalSelect
              size="large"
              placeholder={t('quick.skladPlaceholder')}
              title={t('common.sklad')}
              options={(skladsQuery.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
              loading={skladsQuery.isLoading}
            />
          </Form.Item>
          <Form.Item
            name="date"
            label={t('common.date')}
            rules={[{ required: true, message: t('common.dateRequired') }]}
          >
            <DatePicker
              size="large"
              style={{ width: '100%' }}
              format="DD.MM.YYYY"
              allowClear={false}
              inputReadOnly
            />
          </Form.Item>
          <Form.Item name="msName" label={t('shipments.numberLabel')}>
            <Input size="large" placeholder={t('shipments.numberAuto')} maxLength={50} />
          </Form.Item>
        </Card>

        <Card size="small" title={t('shipments.itemsTitle')} style={{ marginBottom: 12 }}>
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <div style={{ display: 'grid', gap: 12 }}>
                {fields.map((field, index) => {
                  const row = rows[index];
                  const qty = row ? parsedQty(row) : null;
                  const buy = row ? parsedPrice(row.buy) : null;
                  const sell = row ? parsedPrice(row.sell) : null;
                  // «было X»: the remembered buy price for this product at this
                  // store differs from what is typed — the sklad price moved.
                  const memBuy = row?.name
                    ? (lastPricesQuery.data?.[normalizeName(row.name)]?.buyPriceKopeks ?? null)
                    : null;
                  const buyMoved = buy != null && memBuy != null && memBuy !== buy;
                  return (
                    <Card key={field.key} size="small" styles={{ body: { padding: 12 } }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <Typography.Text
                          type="secondary"
                          strong
                          style={{ lineHeight: '40px', minWidth: 20, textAlign: 'right' }}
                        >
                          {index + 1}
                        </Typography.Text>
                        <Form.Item
                          name={[field.name, 'name']}
                          style={{ flex: 1, marginBottom: 8 }}
                          rules={[
                            { required: true, whitespace: true, message: t('common.nameRequired') },
                          ]}
                        >
                          <ModalCombobox
                            size="large"
                            options={productNamesQuery.data ?? []}
                            placeholder={t('shipments.itemNamePlaceholder')}
                            title={t('shipments.itemNamePlaceholder')}
                            maxLength={300}
                            // Auto-fill remembered buy+sell when a product name
                            // is committed (picked or typed).
                            onCommit={(name) => fillFromMemory(field.name, name)}
                          />
                        </Form.Item>
                        <Button
                          icon={<DeleteOutlined />}
                          size="large"
                          onClick={() => remove(field.name)}
                          disabled={fields.length === 1}
                          aria-label={t('common.delete')}
                        />
                      </div>
                      {/* Narrow qty column + one-line labels — a wrapped label
                          used to push its input out of line with the others. */}
                      <div style={{ display: 'grid', gridTemplateColumns: '84px 1fr 1fr', gap: 8 }}>
                        <Form.Item
                          name={[field.name, 'qty']}
                          label={t('shipments.colQty')}
                          style={{ marginBottom: 4 }}
                          rules={[
                            {
                              validator: (_rule, value: string | undefined) =>
                                value && isValidQtyInput(value) && qtyToMilli(value) > 0
                                  ? Promise.resolve()
                                  : Promise.reject(new Error(t('shipments.qtyInvalid'))),
                            },
                          ]}
                        >
                          <Input
                            size="large"
                            inputMode="decimal"
                            placeholder="1,5"
                            autoComplete="off"
                          />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'buy']}
                          label={t('shipments.buyShort')}
                          style={{ marginBottom: 4 }}
                        >
                          <AmountInput />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'sell']}
                          label={t('shipments.sellPriceLabel')}
                          style={{ marginBottom: 4 }}
                        >
                          <AmountInput />
                        </Form.Item>
                      </div>
                      {qty != null && (buy != null || sell != null) ? (
                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                          {buy != null
                            ? `${t('shipments.colMsSum')}: ${formatKopeks(lineSum(qty, buy))}`
                            : ''}
                          {buyMoved ? (
                            <Tag
                              color={buy! > memBuy! ? 'red' : 'green'}
                              style={{ margin: '0 0 0 6px', fontSize: 11 }}
                            >
                              {t('shipments.prevBuyTag', { price: formatKopeks(memBuy!) })}
                            </Tag>
                          ) : null}
                          {buy != null && sell != null ? ' · ' : ''}
                          {sell === 0 && (buy ?? 0) > 0 ? (
                            <Tag color="orange" style={{ margin: '0 0 0 4px' }}>
                              {t('shipments.freeTag')}
                            </Tag>
                          ) : sell != null ? (
                            `${t('shipments.colMySum')}: ${formatKopeks(lineSum(qty, sell))}`
                          ) : (
                            ''
                          )}
                        </Typography.Text>
                      ) : null}
                    </Card>
                  );
                })}
                <Button icon={<PlusOutlined />} size="large" onClick={() => add({})} block>
                  {t('shipments.addItem')}
                </Button>
              </div>
            )}
          </Form.List>
        </Card>

        <Card size="small" title={t('shipments.extraItemsTitle')} style={{ marginBottom: 12 }}>
          <ExtraSkladItems value={extraGroups} onChange={setExtraGroups} storeId={watchedStoreId} />
        </Card>

        {movedInItems.length > 0 ? (
          <Card size="small" title={t('shipments.movedInTitle')} style={{ marginBottom: 12 }}>
            <div style={{ display: 'grid', gap: 10 }}>
              {movedInItems.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'grid', gap: 2 }}>
                    <span>
                      <Typography.Text strong>{item.name}</Typography.Text>{' '}
                      <Tag color="blue" style={{ margin: 0 }}>
                        {t('shipments.movedInTag', { store: item.movedInFromStoreName ?? '' })}
                      </Tag>
                    </span>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {formatQuantityMilli(item.activeQuantityMilli)}
                      {item.buyPriceKopeks != null
                        ? ` · ${t('shipments.buyPriceLabel')}: ${formatKopeks(item.buyPriceKopeks)}`
                        : ''}
                    </Typography.Text>
                  </div>
                  {/* Only the sell price is this store's to set; buy/qty are the transfer's. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Typography.Text type="secondary">{t('shipments.colMyPrice')}</Typography.Text>
                    <div style={{ width: 130 }}>
                      <AmountInput
                        size="middle"
                        value={movedInDraft[item.id] ?? ''}
                        onChange={(value) =>
                          setMovedInDraft((prev) => ({ ...prev, [item.id]: value }))
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ) : null}

        <Card size="small" style={{ marginBottom: 12 }}>
          <div style={{ display: 'grid', gap: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography.Text>{t('shipments.buyTotal')}</Typography.Text>
              <Typography.Text strong>{formatKopeks(totalBuy)}</Typography.Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography.Text>{t('shipments.sellTotal')}</Typography.Text>
              <Typography.Text strong>{formatKopeks(totalSell)}</Typography.Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography.Text>{t('shipments.markupLine')}</Typography.Text>
              <Typography.Text
                strong
                style={{
                  color:
                    sellTotal + extraSellTotal - (buyTotal + extraBuyTotal) >= 0
                      ? OWED_TO_OWNER
                      : OWNER_OWES,
                }}
              >
                {formatKopeks(totalSell - totalBuy)}
              </Typography.Text>
            </div>
          </div>
        </Card>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            icon={<SaveOutlined />}
            size="large"
            htmlType="submit"
            loading={saveMutation.isPending}
          >
            {t('shipments.saveDraft')}
          </Button>
          <Popconfirm
            title={t('shipments.postConfirmTitle')}
            description={t('shipments.postConfirmText', {
              purchase: formatKopeks(totalBuy),
              issue: formatKopeks(totalSell),
            })}
            okText={t('shipments.postButton')}
            cancelText={t('common.cancel')}
            disabled={!allPostable}
            onConfirm={() =>
              form
                .validateFields()
                .then((values) => postMutation.mutate(values))
                .catch(() => undefined)
            }
          >
            <Button
              type="primary"
              icon={<CheckOutlined />}
              size="large"
              disabled={!allPostable}
              loading={postMutation.isPending}
            >
              {t('shipments.postButton')}
            </Button>
          </Popconfirm>
          {shipment ? (
            <Popconfirm
              title={t('shipments.deleteDraftTitle')}
              okText={t('common.delete')}
              cancelText={t('common.cancel')}
              okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
              onConfirm={() => deleteMutation.mutate()}
            >
              <Button danger icon={<DeleteOutlined />} size="large">
                {t('common.delete')}
              </Button>
            </Popconfirm>
          ) : null}
          {!allPostable ? (
            <Typography.Text type="secondary" style={{ alignSelf: 'center' }}>
              {t('shipments.postAllHint')}
            </Typography.Text>
          ) : null}
        </div>
      </Form>
    </div>
  );
}

/** Route component for /shipments/new. */
export function ManualShipmentCreatePage(): JSX.Element {
  return <ManualShipmentEditor />;
}
